/**
 * O sino: quantas mensagens estão esperando resposta.
 *
 * ── Some quando zera, e isso é a regra ───────────────────────────────────
 *
 * Sem mensagem esperando, o sino não aparece. Um sino permanente com "0" ensina
 * a ignorá-lo — e no dia em que o número sobe, ninguém olha. O que aparece é o
 * que precisa de ação.
 *
 * ── Menu aberto mostra o NÚMERO; recolhido, o sino SOME ──────────────────
 *
 * Recolhido, o trilho tem 56px e cada item é um ícone só. Um segundo ícone ali
 * — o sino ao lado do de Atendimento — disputa espaço com o que já está e faz
 * a linha parecer ter duas ações. O aviso vira então uma BOLINHA no canto do
 * próprio ícone de Atendimento, que é para onde a pessoa vai.
 *
 * Por isso este arquivo exporta duas coisas: o `useAvisoDeMensagens`, que traz
 * o número e toca o som, e o `Sino`, que só desenha. Com o menu fechado, quem
 * desenha é o item do menu, com a bolinha — e o som continua tocando, porque
 * ele mora no dado, não no desenho.
 *
 * ── O som mora aqui, e toca quando o número SOBE ─────────────────────────
 *
 * Não a cada resposta do servidor: a mesma contagem chega várias vezes (todo
 * evento do fluxo revalida). Tocar por resposta faria o sino badalar enquanto
 * ninguém escreve nada. Tocar na subida é o que corresponde a "chegou
 * mensagem".
 *
 * A primeira contagem da sessão nunca toca — ela vem de mensagens que já
 * estavam lá. Sino ao abrir o sistema avisaria do passado.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useObservarRecursos } from '@/shared/api/assinaturas';
import { useCaixasDoAtendimento, useNaoLidas } from '@/shared/api/consultas';
import { Icone } from '@/shared/ui';
import {
  definirSilencioDoSino,
  INTERVALO_MINIMO_MS,
  sinoEstaSilenciado,
  tocarSino,
} from '../lib/som-do-sino';
import { reivindicar, TRAVA_DO_SOM } from '../lib/entre-abas';
import { algumaAbaAVista } from '../model/aba-visivel';
import './sino.css';

/** Acima disto o número não cabe no ícone, e vira "9+". */
const TETO_VISIVEL = 9;

function comoTexto(mensagens: number): string {
  return mensagens > TETO_VISIVEL ? `${TETO_VISIVEL}+` : String(mensagens);
}

/** "15 mensagens em 5 conversas" — ver o cabeçalho de `nao-lidas.ts`. */
function dica(mensagens: number, conversas: number): string {
  const m = mensagens === 1 ? '1 mensagem' : `${mensagens} mensagens`;
  const c = conversas === 1 ? '1 conversa' : `${conversas} conversas`;
  return `${m} esperando resposta, em ${c}`;
}

export interface AvisoDeMensagens {
  readonly mensagens: number;
  readonly conversas: number;
}

/**
 * O número, e o som quando ele sobe.
 *
 * Fica separado do desenho porque o aviso existe nas duas formas — número com
 * o menu aberto, bolinha com ele fechado — e o som não pode depender de qual
 * das duas está na tela.
 *
 * ── As caixas são observadas AQUI, em qualquer tela (13/10/2026) ─────────
 *
 * O número só é rebuscado quando o fluxo avisa que uma conversa ou uma caixa
 * mudou (`chavesAtingidasPor`). Essas assinaturas moravam só na tela de
 * atendimento: no funil, nos relatórios ou nos clientes, a mensagem chegava, o
 * número não era rebuscado e o sino não tocava — até a pessoa voltar à aba,
 * quando a rebusca por foco o fazia tocar atrasado. Era o "às vezes toca".
 *
 * `observarCaixas` é o mesmo critério da pilha de notificações: só quem lê
 * conversa. Uma assinatura recusada derruba o fluxo inteiro, e o tempo real
 * das outras telas cairia junto.
 */
export function useAvisoDeMensagens(observarCaixas = false): AvisoDeMensagens {
  const caixas = useCaixasDoAtendimento(observarCaixas);
  const assinaturas = useMemo(
    () =>
      observarCaixas
        ? (caixas.data?.itens ?? []).map((caixa) => `inbox:${caixa.referencia_externa}`)
        : [],
    [observarCaixas, caixas.data],
  );
  useObservarRecursos(assinaturas);

  const consulta = useNaoLidas();
  const mensagens = consulta.data?.mensagens ?? 0;
  const conversas = consulta.data?.conversas ?? 0;

  /*
   * `null` até a primeira contagem chegar — e é o que impede o sino de tocar
   * na abertura do sistema. `0` seria diferente: significaria "já contei, e
   * não havia nada", e aí a primeira mensagem tocaria, como deve.
   */
  const anterior = useRef<number | null>(null);

  useEffect(() => {
    if (consulta.data === undefined) return;

    const antes = anterior.current;
    anterior.current = mensagens;

    // Só na SUBIDA. Ver o cabeçalho: a mesma contagem chega muitas vezes.
    if (antes !== null && mensagens > antes) void tocarUmaVezEntreAbas();
  }, [mensagens, consulta.data]);

  return { mensagens, conversas };
}

/**
 * Com várias abas abertas, a mesma subida chega a todas — e o som tocava uma
 * vez por aba (ver `entre-abas.ts`).
 *
 * A aba À VISTA toca sempre: é nela que a pessoa clicou, e o navegador só
 * toca áudio em aba que recebeu clique. Uma aba escondida que ganhasse a vez
 * tocaria em silêncio — foi o que aconteceu ao testar, em 22/10/2026, com o
 * sorteio entre todas as abas. As escondidas só tocam quando nenhuma está à
 * vista, e aí uma só, a que pega a trava.
 */
async function tocarUmaVezEntreAbas(): Promise<void> {
  if (sinoEstaSilenciado()) return;
  if (document.visibilityState === 'visible') {
    tocarSino();
    return;
  }
  if (await algumaAbaAVista()) return;
  if (await reivindicar(TRAVA_DO_SOM, INTERVALO_MINIMO_MS)) tocarSino();
}

/**
 * O sino desenhado — só com o menu ABERTO.
 *
 * Recebe o número em vez de buscá-lo: quem busca é o `useAvisoDeMensagens`, no
 * menu, que precisa do valor mesmo quando este componente não está na tela.
 *
 * ── Clicar no sino liga e desliga o som (30/09/2026) ─────────────────────
 *
 * Antes o clique levava à fila e havia um "silenciar" escrito ao lado. O texto
 * quebrava a linha do Atendimento no trilho estreito, e ir à fila já é o clique
 * no próprio Atendimento. Agora o sino É o interruptor do som: riscado quando
 * está mudo, com o número dentro como antes.
 *
 * O silenciar existe porque o sino toca em sala compartilhada. Som sem
 * desligar é o que faz a pessoa desligar o som do computador inteiro — e aí
 * ela perde também o aviso que queria.
 *
 * Sem mensagem, o sino continua sumindo — MENOS quando está mudo: o sino
 * riscado fica, porque é o único lugar onde a pessoa vê que desligou o som, e
 * de onde religa. Sumir com ele esconderia que o aviso não vai tocar.
 */
export function Sino({
  mensagens,
  conversas,
}: {
  mensagens: number;
  conversas: number;
}): ReactNode {
  const [silenciado, definirSilenciado] = useState(sinoEstaSilenciado);

  if (mensagens === 0 && !silenciado) return null;

  const acao = silenciado ? 'Voltar a tocar o sino' : 'Silenciar o sino';
  const nome = mensagens > 0 ? `${acao}. ${dica(mensagens, conversas)}` : `${acao}.`;

  return (
    <button
      type="button"
      className="sino"
      data-mudo={silenciado ? 'sim' : undefined}
      aria-label={nome}
      title={nome}
      onClick={() => {
        const novo = !silenciado;
        definirSilencioDoSino(novo);
        definirSilenciado(novo);
      }}
    >
      <span className="sino__icone" aria-hidden="true">
        <Icone nome={silenciado ? 'sino-mudo' : 'sino'} />
        {mensagens > 0 && <span className="sino__numero">{comoTexto(mensagens)}</span>}
      </span>
    </button>
  );
}
